import { formatTimer, getTimerTone } from './format';
import type { TimerSnapshot, UserSettings } from './types';

export interface PipActions {
  togglePause(): void;
  addMinute(): void;
  addFiveMinutes(): void;
  reset(): void;
}

export interface PipManagerOptions {
  getSnapshot: () => TimerSnapshot;
  getSettings: () => UserSettings;
  actions: PipActions;
  onWindowStateChange: (isOpen: boolean) => void;
  onSizeChange: (width: number, height: number) => void;
}

export function supportsDocumentPip(target: Window = window): boolean {
  return 'documentPictureInPicture' in target && Boolean(target.documentPictureInPicture);
}

export class DocumentPipManager {
  private pipWindow: Window | null = null;
  private timerNode: HTMLElement | null = null;
  private pauseButton: HTMLButtonElement | null = null;
  private controlsNode: HTMLElement | null = null;
  private pureDisplay = true;
  private hideControlsTimer: number | null = null;

  constructor(private readonly options: PipManagerOptions) {}

  get isOpen(): boolean {
    return Boolean(this.pipWindow && !this.pipWindow.closed);
  }

  get isSupported(): boolean {
    return supportsDocumentPip();
  }

  open(): Promise<boolean> {
    if (this.isOpen) {
      this.pipWindow?.focus();
      return Promise.resolve(true);
    }

    const api = window.documentPictureInPicture;
    if (!api) return Promise.resolve(false);

    const settings = this.options.getSettings();
    // Keep this request as the very first asynchronous browser operation. The
    // caller invokes open() directly in the click handler, preserving activation.
    let request: Promise<Window>;
    try {
      request = api.requestWindow({
        width: settings.pipWidth,
        height: settings.pipHeight,
        preferInitialWindowPlacement: true,
      });
    } catch {
      return Promise.resolve(false);
    }

    return request.then((pipWindow) => {
      this.pipWindow = pipWindow;
      this.pureDisplay = true;
      this.renderWindow(pipWindow);
      this.options.onWindowStateChange(true);
      this.update(this.options.getSnapshot());
      return true;
    }).catch(() => false);
  }

  close(): void {
    this.pipWindow?.close();
  }

  toggleDisplayMode(): void {
    if (!this.isOpen) return;
    this.pureDisplay = !this.pureDisplay;
    this.applyDisplayMode();
  }

  update(snapshot: TimerSnapshot): void {
    if (!this.isOpen || !this.timerNode) return;
    this.timerNode.textContent = formatTimer(snapshot);
    this.timerNode.dataset.tone = getTimerTone(snapshot, this.options.getSettings().thresholds);
    this.timerNode.classList.toggle('is-long', formatTimer(snapshot).length >= 7);
    if (this.pauseButton) {
      this.pauseButton.textContent = snapshot.phase === 'paused' ? '繼續' : '暫停';
      this.pauseButton.disabled = snapshot.phase === 'idle';
    }
  }

  private renderWindow(pipWindow: Window): void {
    const document = pipWindow.document;
    document.documentElement.lang = 'zh-Hant';
    document.title = 'TeamsTimer 置頂計時器';
    document.head.replaceChildren();
    document.body.replaceChildren();

    const meta = document.createElement('meta');
    meta.name = 'viewport';
    meta.content = 'width=device-width, initial-scale=1';
    const style = document.createElement('style');
    style.textContent = pipStyles;
    document.head.append(meta, style);

    const shell = document.createElement('main');
    shell.className = 'pip-shell is-pure';
    shell.setAttribute('aria-label', 'TeamsTimer 置頂計時器');
    shell.innerHTML = `
      <div class="pip-time" role="timer" aria-live="off">00:00</div>
      <div class="pip-controls" aria-label="計時器控制列">
        <button type="button" data-action="pause">暫停</button>
        <button type="button" data-action="plus-one">＋1 分鐘</button>
        <button type="button" data-action="plus-five">＋5 分鐘</button>
        <button type="button" data-action="return" class="subtle">回到設定</button>
      </div>
    `;
    document.body.append(shell);

    this.timerNode = shell.querySelector('.pip-time');
    this.controlsNode = shell.querySelector('.pip-controls');
    this.pauseButton = shell.querySelector('[data-action="pause"]');

    this.pauseButton?.addEventListener('click', () => this.options.actions.togglePause());
    shell.querySelector('[data-action="plus-one"]')?.addEventListener('click', () => this.options.actions.addMinute());
    shell.querySelector('[data-action="plus-five"]')?.addEventListener('click', () => this.options.actions.addFiveMinutes());
    shell.querySelector('[data-action="return"]')?.addEventListener('click', () => {
      pipWindow.close();
      window.focus();
    });

    shell.addEventListener('dblclick', (event) => {
      if ((event.target as HTMLElement).closest('button')) return;
      this.toggleDisplayMode();
    });
    shell.addEventListener('pointerenter', () => this.showControls());
    shell.addEventListener('pointermove', () => this.showControls());
    shell.addEventListener('pointerleave', () => this.scheduleControlsHide());
    pipWindow.addEventListener('keydown', (event) => this.handleKeydown(event));
    pipWindow.addEventListener('pagehide', () => this.handleWindowClosed(pipWindow), { once: true });
    this.applyDisplayMode();
  }

  private handleKeydown(event: KeyboardEvent): void {
    if ((event.target as HTMLElement | null)?.matches('input, textarea, select')) return;
    const key = event.key.toLowerCase();
    if (event.code === 'Space') {
      event.preventDefault();
      this.options.actions.togglePause();
    } else if (key === 'r') {
      this.options.actions.reset();
    } else if (key === '1') {
      this.options.actions.addMinute();
    } else if (key === '5') {
      this.options.actions.addFiveMinutes();
    } else if (key === 'f') {
      this.toggleDisplayMode();
    } else if (event.key === 'Escape') {
      this.close();
      window.focus();
    }
  }

  private applyDisplayMode(): void {
    const shell = this.timerNode?.closest('.pip-shell');
    shell?.classList.toggle('is-pure', this.pureDisplay);
    shell?.classList.toggle('is-operation', !this.pureDisplay);
    if (this.pureDisplay) this.scheduleControlsHide();
    else this.showControls();
  }

  private showControls(): void {
    if (this.hideControlsTimer !== null && this.pipWindow) {
      this.pipWindow.clearTimeout(this.hideControlsTimer);
      this.hideControlsTimer = null;
    }
    this.controlsNode?.classList.add('is-visible');
  }

  private scheduleControlsHide(): void {
    if (!this.pureDisplay || !this.pipWindow) return;
    if (this.hideControlsTimer !== null) this.pipWindow.clearTimeout(this.hideControlsTimer);
    this.hideControlsTimer = this.pipWindow.setTimeout(() => {
      this.controlsNode?.classList.remove('is-visible');
      this.hideControlsTimer = null;
    }, 1_500);
  }

  private handleWindowClosed(closedWindow: Window): void {
    const width = Math.max(320, Math.round(closedWindow.outerWidth || closedWindow.innerWidth));
    const height = Math.max(180, Math.round(closedWindow.outerHeight || closedWindow.innerHeight));
    this.options.onSizeChange(width, height);
    this.pipWindow = null;
    this.timerNode = null;
    this.pauseButton = null;
    this.controlsNode = null;
    this.options.onWindowStateChange(false);
  }
}

const pipStyles = `
  :root { color-scheme: dark; font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Noto Sans TC", sans-serif; }
  * { box-sizing: border-box; }
  html, body { width: 100%; height: 100%; margin: 0; overflow: hidden; background: #050707; }
  body { user-select: none; }
  .pip-shell { position: relative; display: flex; align-items: center; justify-content: center; width: 100vw; height: 100vh; padding: 12px; background: #050707; }
  .pip-time { max-width: calc(100vw - 24px); color: #55d6a5; font-family: "SFMono-Regular", Consolas, "Liberation Mono", monospace; font-size: min(34vw, 58vh); font-weight: 650; line-height: .88; letter-spacing: -.07em; white-space: nowrap; transition: color 280ms ease; font-variant-numeric: tabular-nums lining-nums; }
  .pip-time.is-long { font-size: min(26vw, 48vh); }
  .pip-time[data-tone="yellow"] { color: #e9bd62; }
  .pip-time[data-tone="red"] { color: #f36c71; }
  .pip-controls { position: absolute; left: 50%; bottom: 10px; z-index: 2; display: flex; gap: 6px; width: max-content; max-width: calc(100vw - 20px); padding: 7px; overflow-x: auto; border: 1px solid rgba(255,255,255,.11); border-radius: 12px; background: rgba(24,28,28,.88); box-shadow: 0 10px 30px rgba(0,0,0,.34); opacity: 0; pointer-events: none; transform: translate(-50%, 8px); transition: opacity 180ms ease, transform 180ms ease; backdrop-filter: blur(14px); }
  .pip-controls.is-visible, .is-operation .pip-controls { opacity: 1; pointer-events: auto; transform: translate(-50%, 0); }
  button { min-height: 36px; padding: 0 12px; border: 1px solid rgba(255,255,255,.09); border-radius: 9px; color: #e8efec; background: #27302e; font: inherit; font-size: 13px; font-weight: 650; white-space: nowrap; cursor: pointer; transition: background 140ms ease, transform 100ms ease; }
  button:hover { background: #34403d; }
  button:active { transform: scale(.97); }
  button:focus-visible { outline: 2px solid #79dfb6; outline-offset: 2px; }
  button:disabled { opacity: .45; cursor: not-allowed; }
  button.subtle { color: #bbc5c1; background: #202424; }
  @media (prefers-reduced-motion: reduce) { * { transition: none !important; } }
`;
